/* Use of Funds Schedule card — line-item breakdown of how loan proceeds
 * will be used. Each line accepts a description, amount, category, notes,
 * and an arbitrary number of supporting documents (invoices/quotes/etc.).
 *
 * Mirrors the UX of DebtScheduleCard. Used on:
 *   - Authenticated review screen (broker / underwriter / admin)
 *   - Public borrower completion link (read+write)
 *   - Public shared underwriting link (read-only display + attachment download)
 *
 * Props:
 *   applicationId            (auth mode only) — the application id
 *   token                    (public mode) — borrower-link token; when set,
 *                             public borrower endpoints are used
 *   sharedUnderwritingToken  (public read-only) — when set, UI hides edit
 *                             controls and uses the shared-uw download
 *                             endpoint for attachments
 *   items                    array of {id, description, amount, category,
 *                             notes, attachments}
 *   onSave                   callback() after the schedule is updated
 *   authToken                JWT (auth mode)
 *   amountRequested          numeric — used to render running-total mismatch
 *                             warning
 *   readOnly                 boolean — force read-only mode
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  DollarSign, Plus, Trash2, Loader2, Pencil, Paperclip, X, Download,
  Upload, FileText, AlertCircle, Link as LinkIcon, Copy, Check,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 25 * 1024 * 1024;

export const USE_OF_FUNDS_CATEGORIES = [
  'Refinance Existing Debt',
  'Working Capital',
  'Hiring / Payroll',
  'Marketing',
  'Equipment',
  'Inventory',
  'Real Estate',
  'Other',
];

const fmtCurrency = (v) => (v != null && v !== '')
  ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  : '—';

const UseOfFundsScheduleCard = ({
  applicationId,
  token,
  linkKind = 'borrower', // 'borrower' | 'use_of_funds' — determines public URL prefix
  sharedUnderwritingToken,
  items: initialItems = [],
  onSave,
  authToken,
  amountRequested = 0,
  readOnly = false,
}) => {
  const [mode, setMode] = useState('view');
  const [items, setItems] = useState(initialItems || []);
  const [saving, setSaving] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const fileInputRefs = useRef({});

  useEffect(() => { setItems(initialItems || []); }, [initialItems]);

  const isShared = !!sharedUnderwritingToken;
  const isPublicLink = !!token;
  const isAuth = !!applicationId && !isPublicLink && !isShared;
  const canEdit = !readOnly && !isShared && (isAuth || isPublicLink);

  // Public URL prefix — differs based on which kind of public link the token
  // belongs to. Keeps the borrower completion flow (borrower_links) and the
  // dedicated UoF share (use_of_funds_links) cleanly separated.
  const publicPrefix = linkKind === 'use_of_funds'
    ? `${BACKEND_URL}/api/use-of-funds-link/${token}`
    : `${BACKEND_URL}/api/public/borrower-use-of-funds/${token}`;
  const publicSaveUrl = publicPrefix;
  const publicPdfUrl = `${publicPrefix}/pdf`;

  const hasData = items.length > 0;
  const total = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
  const requested = Number(amountRequested) || 0;
  const mismatch = requested > 0 && hasData && Math.abs(total - requested) > 1;

  const headers = () => {
    const h = {};
    if (authToken) h['Authorization'] = `Bearer ${authToken}`;
    return h;
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      id: '', // backend will assign on save
      description: '',
      amount: null,
      category: '',
      notes: '',
      attachments: [],
    }]);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip empty rows
      const cleaned = items.filter(it => (it.description || '').trim() || it.amount);
      const url = isPublicLink
        ? publicSaveUrl
        : `${BACKEND_URL}/api/applications/${applicationId}/use-of-funds`;
      const resp = await axios.put(url, { items: cleaned }, { headers: headers() });
      setItems(resp.data?.items || cleaned);
      setMode('view');
      if (onSave) onSave(resp.data?.items || cleaned);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save Use of Funds schedule');
    } finally {
      setSaving(false);
    }
  };

  const triggerUpload = (itemId) => {
    const ref = fileInputRefs.current[itemId];
    if (ref) ref.click();
  };

  const handleAttachmentUpload = async (itemId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      alert('File exceeds the 25 MB limit');
      e.target.value = '';
      return;
    }
    setUploadingItemId(itemId);
    try {
      const form = new FormData();
      form.append('file', file);
      const url = isPublicLink
        ? `${publicPrefix}/${itemId}/upload`
        : `${BACKEND_URL}/api/applications/${applicationId}/use-of-funds/${itemId}/upload`;
      const resp = await axios.post(url, form, { headers: headers() });
      // Append the new attachment locally
      setItems(prev => prev.map(it => it.id === itemId
        ? { ...it, attachments: [...(it.attachments || []), resp.data.attachment] }
        : it));
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingItemId(null);
      e.target.value = '';
    }
  };

  const handleAttachmentDelete = async (itemId, fileId) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      const url = isPublicLink
        ? `${publicPrefix}/${itemId}/attachment/${fileId}`
        : `${BACKEND_URL}/api/applications/${applicationId}/use-of-funds/${itemId}/attachment/${fileId}`;
      await axios.delete(url, { headers: headers() });
      setItems(prev => prev.map(it => it.id === itemId
        ? { ...it, attachments: (it.attachments || []).filter(a => a.file_id !== fileId) }
        : it));
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to remove attachment');
    }
  };

  const attachmentDownloadUrl = (itemId, fileId) => {
    if (isShared) {
      return `${BACKEND_URL}/api/shared/underwriting/${sharedUnderwritingToken}/use-of-funds/${itemId}/attachment/${fileId}`;
    }
    if (isPublicLink) {
      return `${publicPrefix}/${itemId}/attachment/${fileId}`;
    }
    return `${BACKEND_URL}/api/applications/${applicationId}/use-of-funds/${itemId}/attachment/${fileId}`;
  };

  const handleDownloadAttachment = async (itemId, attachment) => {
    try {
      const url = attachmentDownloadUrl(itemId, attachment.file_id);
      const resp = await axios.get(url, { headers: isAuth ? headers() : undefined, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: attachment.content_type || 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 250);
    } catch (err) {
      alert('Failed to download attachment');
    }
  };

  const handleDownloadSchedulePdf = async () => {
    try {
      const reqHeaders = {};
      let url;
      if (isShared) {
        url = `${BACKEND_URL}/api/shared/underwriting/${sharedUnderwritingToken}/use-of-funds/pdf`;
      } else if (isPublicLink) {
        url = publicPdfUrl;
      } else {
        url = `${BACKEND_URL}/api/applications/use-of-funds-pdf/${applicationId}`;
        if (authToken) reqHeaders['Authorization'] = `Bearer ${authToken}`;
      }
      const resp = await axios.get(url, { headers: reqHeaders, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `use_of_funds_${applicationId || token || 'schedule'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 250);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const handleGenerateShareLink = async () => {
    if (!isAuth) return;
    setGeneratingLink(true);
    setShareLink('');
    setCopied(false);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/applications/${applicationId}/use-of-funds/generate-link`,
        {},
        { headers: headers() },
      );
      setShareLink(`${window.location.origin}/use-of-funds/${resp.data.token}`);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to generate share link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('Failed to copy link');
    }
  };

  const renderViewRow = (item) => (
    <div key={item.id} className="bg-white border border-emerald-200 rounded-lg p-3 space-y-2" data-testid={`uof-view-row-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900 truncate" data-testid={`uof-view-desc-${item.id}`}>
              {item.description || <span className="italic text-slate-400">No description</span>}
            </span>
            {item.category && (
              <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full font-semibold">{item.category}</span>
            )}
          </div>
          {item.notes && <p className="text-xs text-slate-500 mt-1">{item.notes}</p>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-emerald-700" data-testid={`uof-view-amount-${item.id}`}>{fmtCurrency(item.amount)}</div>
        </div>
      </div>
      {(item.attachments || []).length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-slate-100">
          <Paperclip className="h-3 w-3 text-slate-400" />
          {(item.attachments || []).map(a => (
            <button
              key={a.file_id}
              type="button"
              onClick={() => handleDownloadAttachment(item.id, a)}
              className="text-[11px] px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded inline-flex items-center gap-1 max-w-[220px] truncate"
              data-testid={`uof-attachment-${a.file_id}`}
              title={a.filename}
            >
              <FileText className="h-3 w-3 shrink-0" />
              <span className="truncate">{a.filename}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderEditRow = (item, idx) => {
    const itemSaved = !!item.id; // backend has assigned an id → can attach files
    return (
      <div key={item.id || idx} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`uof-edit-row-${idx}`}>
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-12 md:col-span-5">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Description</Label>
            <Input
              value={item.description || ''}
              onChange={e => updateItem(idx, 'description', e.target.value)}
              placeholder="e.g. Refinance OnDeck loan"
              className="h-9 text-sm"
              data-testid={`uof-input-desc-${idx}`}
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Amount</Label>
            <Input
              type="number"
              step="0.01"
              value={item.amount ?? ''}
              onChange={e => updateItem(idx, 'amount', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="0.00"
              className="h-9 text-sm"
              data-testid={`uof-input-amount-${idx}`}
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Category</Label>
            <select
              value={item.category || ''}
              onChange={e => updateItem(idx, 'category', e.target.value)}
              className="h-9 text-sm w-full rounded-md border border-input bg-background px-2"
              data-testid={`uof-input-category-${idx}`}
            >
              <option value="">Select...</option>
              {USE_OF_FUNDS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-span-12 md:col-span-1 flex md:items-end justify-end">
            <Button
              size="sm"
              variant="ghost"
              className="h-9 text-red-600 hover:bg-red-50"
              onClick={() => removeItem(idx)}
              data-testid={`uof-remove-row-${idx}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Notes (optional)</Label>
          <Input
            value={item.notes || ''}
            onChange={e => updateItem(idx, 'notes', e.target.value)}
            placeholder="Anything else the underwriter should know"
            className="h-8 text-sm"
            data-testid={`uof-input-notes-${idx}`}
          />
        </div>
        {/* Attachments only after the row has been saved (so it has an id) */}
        {itemSaved && (
          <div className="pt-1 border-t border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Supporting Documents</Label>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => triggerUpload(item.id)}
                disabled={uploadingItemId === item.id}
                data-testid={`uof-attach-btn-${idx}`}
              >
                {uploadingItemId === item.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                Attach Invoice / Quote
              </Button>
              <input
                ref={el => (fileInputRefs.current[item.id] = el)}
                type="file"
                className="hidden"
                onChange={e => handleAttachmentUpload(item.id, e)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.csv,.txt"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {(item.attachments || []).length === 0 && (
                <span className="text-[11px] text-slate-400 italic">No attachments yet</span>
              )}
              {(item.attachments || []).map(a => (
                <div key={a.file_id} className="inline-flex items-center gap-1 bg-slate-100 rounded px-1.5 py-0.5">
                  <button
                    type="button"
                    className="text-[11px] text-slate-700 hover:underline inline-flex items-center gap-1 max-w-[200px] truncate"
                    onClick={() => handleDownloadAttachment(item.id, a)}
                    title={a.filename}
                  >
                    <FileText className="h-3 w-3 shrink-0" />
                    <span className="truncate">{a.filename}</span>
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      className="text-slate-400 hover:text-red-600"
                      onClick={() => handleAttachmentDelete(item.id, a.file_id)}
                      data-testid={`uof-remove-attachment-${a.file_id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {!itemSaved && (
          <p className="text-[11px] text-slate-400 italic pt-1 border-t border-slate-100">
            Save the schedule first, then you'll be able to attach invoices to this line.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className={`border rounded-xl p-4 ${hasData ? 'bg-emerald-50/40 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-testid="use-of-funds-card">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <DollarSign className={`h-4 w-4 ${hasData ? 'text-emerald-600' : 'text-slate-500'}`} />
          <h4 className={`text-sm font-bold ${hasData ? 'text-emerald-900' : 'text-slate-700'}`}>Use of Funds Schedule</h4>
          {hasData && (
            <span className="text-[11px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">
              {items.length} {items.length === 1 ? 'line' : 'lines'} · {fmtCurrency(total)}
            </span>
          )}
        </div>
        {(hasData || canEdit) && (
          <div className="flex items-center gap-1.5">
            {hasData && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleDownloadSchedulePdf} data-testid="uof-download-pdf-btn">
                <Download className="h-3 w-3 mr-1" /> Download PDF
              </Button>
            )}
            {isAuth && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={handleGenerateShareLink}
                disabled={generatingLink}
                data-testid="uof-share-link-btn"
              >
                {generatingLink ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <LinkIcon className="h-3 w-3 mr-1" />}
                Share Link
              </Button>
            )}
            {canEdit && mode === 'view' && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { if (!hasData) addItem(); setMode('edit'); }} data-testid="uof-edit-btn">
                <Pencil className="h-3 w-3 mr-1" /> {hasData ? 'Edit' : 'Add Data'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Share link display panel — visible after admin clicks "Share Link" */}
      {shareLink && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-testid="uof-share-link-panel">
          <div className="flex items-center gap-2 flex-wrap">
            <LinkIcon className="h-4 w-4 text-blue-600 shrink-0" />
            <p className="text-xs font-semibold text-blue-900">Share this link with the borrower or broker:</p>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Input
              readOnly
              value={shareLink}
              className="text-xs bg-white flex-1"
              onFocus={(e) => e.target.select()}
              data-testid="uof-share-link-url"
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs shrink-0"
              onClick={handleCopyShareLink}
              data-testid="uof-copy-link-btn"
            >
              {copied ? <><Check className="h-3 w-3 mr-1 text-emerald-600" /> Copied</> : <><Copy className="h-3 w-3 mr-1" /> Copy</>}
            </Button>
          </div>
        </div>
      )}

      {/* Running-total mismatch banner */}
      {mismatch && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2 text-xs text-amber-900" data-testid="uof-mismatch-banner">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
          <div>
            <strong>Total ({fmtCurrency(total)}) doesn't match requested amount ({fmtCurrency(requested)}).</strong>
            <span className="block text-amber-700">Difference: {fmtCurrency(Math.abs(total - requested))}</span>
          </div>
        </div>
      )}

      {mode === 'view' && !hasData && (
        <p className="text-sm text-slate-500 italic" data-testid="uof-empty-state">
          No Use of Funds entries yet. {canEdit && 'Click "Add Data" to break down how the loan proceeds will be used.'}
        </p>
      )}

      {mode === 'view' && hasData && (
        <div className="space-y-2" data-testid="uof-view-list">
          {items.map(renderViewRow)}
          <div className="flex justify-end pt-1">
            <div className="text-sm font-bold text-emerald-900" data-testid="uof-total-display">
              Total: {fmtCurrency(total)}{requested > 0 && <span className="text-slate-500 font-normal ml-2">/ Requested {fmtCurrency(requested)}</span>}
            </div>
          </div>
        </div>
      )}

      {mode === 'edit' && (
        <div className="space-y-2" data-testid="uof-edit-list">
          {items.map(renderEditRow)}
          {items.length === 0 && (
            <p className="text-sm text-slate-500 italic">Click "Add Line" to start.</p>
          )}
          <div className="flex items-center justify-between pt-2">
            <Button size="sm" variant="outline" onClick={addItem} data-testid="uof-add-line-btn">
              <Plus className="h-3 w-3 mr-1" /> Add Line
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-700" data-testid="uof-running-total">
                Running total: {fmtCurrency(total)}
              </span>
              <Button variant="outline" size="sm" onClick={() => { setItems(initialItems || []); setMode('view'); }} disabled={saving}>Cancel</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSave} disabled={saving} data-testid="uof-save-btn">
                {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving...</> : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UseOfFundsScheduleCard;
